import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon, HeartHandshakeIcon, QuoteIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { SectionHeading } from './SectionHeading';
const testimonials = [['MG', '“Por primera vez en años entré al lunes sin ese nudo en el estómago. Los guiones de la Dra. Karito son exactamente lo que necesitaba.”', 'María G. · Psicóloga clínica, México', 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=160&q=85'], ['LP', '“El protocolo para el llanto incontenible me salvó en una sesión que se me estaba yendo de las manos.”', 'Laura P. · Terapeuta, Bogotá', 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=160&q=85'], ['RM', '“Las Preguntas de Fuego eran mi pesadilla. Ahora las recibo con calma.”', 'Roberto M. · Psicólogo, Buenos Aires', 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=160&q=85'], ['SC', '“Mis pacientes notan algo distinto en mí. Yo llego más presente y más segura.”', 'Sofía C. · Psicóloga clínica, Bogotá', 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=160&q=85']];
const painPoints = ['El silencio se extiende y tu mente se queda en blanco.', 'Una pregunta directa te deja sin palabras.', 'El paciente explota y vos te congelás.', 'Terminás la sesión rumiando lo que debiste haber dicho.'];
const faqs = [['¿Sirve si tengo muchos años de experiencia?', 'Sí. El bloqueo no discrimina antigüedad. El Oráculo te da lenguaje claro para lo que ya sabés, en el momento preciso.'], ['¿Cómo recibo el material?', 'Es 100% digital. Recibís el acceso a los 6 tomos y los 6 bonos en PDF inmediatamente después de la compra.'], ['¿Qué lo diferencia de un libro de técnicas?', 'No es teoría para interpretar después: son guiones y protocolos listos para consultar en la sesión de hoy.'], ['¿Tiene garantía real?', 'Sí. Tenés 7 días de garantía total. Si no es para vos, escribís y se devuelve el 100%.'], ['¿Funciona para mi país?', 'Está escrito en español neutro, pensado para terapeutas de toda habla hispana.'], ['¿Necesito formación específica?', 'Está diseñado para terapeutas y psicólogos con formación universitaria o en curso. Complementa, no reemplaza, tu formación clínica.']];
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-[#DDD7E6]" data-id="element-85" data-mp="1u0w4w5b712vg">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-4 py-5 text-left font-semibold text-[#24212A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6D28D9]" data-id="element-86" data-mp="3sf7juvo91wo4">
        {question}
        <ChevronDownIcon className={`h-5 w-5 shrink-0 text-[#6D28D9] transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-87" data-mp="1f4646abghbw8" />
      </button>
      <AnimatePresence initial={false} data-id="element-88" data-mp="1jxiinrmf82b9">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-89" data-mp="1nvg9eyvuawgr">
            <p className="pb-5 pr-8 text-sm leading-6 text-[#5E5968]" data-id="element-90" data-mp="1568jfwst1x8d">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Testimonials() {
  return <section className="bg-[#F2EFF6] px-5 py-14 sm:px-8 sm:py-20" data-id="element-91" data-mp="2gkvqa5a6rjkl">
      <div className="mx-auto max-w-6xl" data-id="element-92" data-mp="2gn89smvle1zr">
        <SectionHeading centered eyebrow="Terapeutas que ya lo tienen" title="La calma también se entrena." data-id="element-93" data-mp="2f5axztqi81p8" />
        <div className="mt-9 grid gap-4 md:grid-cols-2" data-id="element-94" data-mp="2a7htr68rhhrl">
          {testimonials.map(([initials, quote, author, imageUrl]) => <article key={author} className="border border-[#DDD7E6] bg-[#FFFDFB] p-5 sm:p-6" data-id="element-95" data-mp="2k2tatv7n712i">
              <div className="flex items-center justify-between" data-id="element-96" data-mp="2y1j758mn0wpt">
                <div className="flex items-center gap-3" data-id="element-97" data-mp="2t4ixknqkxbty">
                  <img src={imageUrl} alt={`Retrato ilustrativo de ${author.split(' · ')[0]}`} className="h-11 w-11 rounded-full border border-[#D4C8E8] object-cover" data-id="element-98" data-mp="2t1wqsqbij0cs" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#6D28D9]" data-id="element-99" data-mp="3gstxnoogb7h6">
                    Testimonio verificado
                  </span>
                </div>
                <div className="text-xs tracking-wider text-[#6D28D9]" aria-label="5 de 5 estrellas" data-id="element-100" data-mp="3vfsn5e3qzrt9">
                  ★★★★★
                </div>
              </div>
              <QuoteIcon className="mt-5 h-5 w-5 text-[#6D28D9]" aria-hidden="true" data-id="element-101" data-mp="3mekpmy922v08" />
              <blockquote className="mt-2 font-serif text-xl leading-7 text-[#24212A]" data-id="element-102" data-mp="3qjaisfcbxvhg">
                {quote}
              </blockquote>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#5E5968]" data-id="element-103" data-mp="3hh191rovy654">
                {author}
              </p>
            </article>)}
        </div>
      </div>
    </section>;
}
export function PainSection() {
  return <section className="bg-[#FFFDFB] px-5 py-14 sm:px-8 sm:py-20" data-id="element-104" data-mp="37on5cte8ypgb">
      <div className="mx-auto grid max-w-5xl gap-9 md:grid-cols-[.8fr_1.2fr] md:items-center" data-id="element-105" data-mp="2trzzg14bgoy9">
        <SectionHeading eyebrow="El ciclo que conocés muy bien" title={<>¿Cuántas sesiones más vas a salir sintiéndote así?</>} intro="No es falta de formación. Es falta de un sistema para el instante en que la presión aparece." data-id="element-106" data-mp="2yp0365dp1fm7" />
        <div className="space-y-3" data-id="element-107" data-mp="ujqg607phdeg">
          {painPoints.map(point => <p key={point} className="flex gap-3 border border-[#E3DDEA] bg-[#F8F7FB] p-4 text-sm font-medium leading-6 text-[#514B5A]" data-id="element-108" data-mp="uhnh6qoks9xl">
              <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#6D28D9]" aria-hidden="true" data-id="element-109" data-mp="1n7w2usiblcgx" />
              {point}
            </p>)}
          <p className="pt-3 font-serif text-2xl text-[#6D28D9]" data-id="element-110" data-mp="1e6638zeab782">
            ¿Y si este fuera el último lunes que llegás así a tu consultorio?
          </p>
        </div>
      </div>
    </section>;
}
export function GuaranteeAndFAQ() {
  return <>
      <section className="bg-[#6D28D9] px-5 py-14 text-[#FFFDFB] sm:px-8 sm:py-20" data-id="element-111" data-mp="3tyvj1lgk2qj5">
        <div className="mx-auto max-w-4xl text-center" data-id="element-112" data-mp="29hme267di33e">
          <ShieldCheckIcon className="mx-auto h-10 w-10 text-white" aria-hidden="true" data-id="element-113" data-mp="29ixebw1m0spr" />
          <p className="mt-5 text-xs font-bold uppercase tracking-[.2em] text-[#E9DFFD]" data-id="element-114" data-mp="2ihsmves4mew8">
            Garantía de 7 días · Sin preguntas
          </p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight" data-id="element-115" data-mp="2n9ang1q8mdgo">
            Abrís los tomos. Los usás. Decidís con calma.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[#F0EAFE]" data-id="element-116" data-mp="2x6ow3f5x5xyf">
            Si en 7 días sentís que no es para vos, escribís y recibís la
            devolución del 100%. Sin formularios, sin drama.
          </p>
        </div>
      </section>
      <section className="bg-[#FFFDFB] px-5 py-14 sm:px-8 sm:py-20" data-id="element-117" data-mp="366treqxu95w8">
        <div className="mx-auto max-w-3xl" data-id="element-118" data-mp="17ssdqkba25ok">
          <SectionHeading centered eyebrow="Respuestas claras" title="Lo que más nos preguntan" data-id="element-119" data-mp="17ttrwxnlb8ai" />
          <div className="mt-7" data-id="element-120" data-mp="swaezm64jzlr">
            {faqs.map(([question, answer]) => <FAQItem key={question} question={question} answer={answer} data-id="element-121" data-mp="ny8tyvp51q7n" />)}
          </div>
        </div>
      </section>
    </>;
}
export function CostAndFinalCTA() {
  const risks = ['52 momentos de parálisis por año', 'Horas de sueño y paz mental perdidas', 'Alianzas terapéuticas en riesgo', 'Desgaste profesional acumulado'];
  return <>
      <section className="bg-[#24212A] px-5 py-14 text-[#FFFDFB] sm:px-8 sm:py-20" data-id="element-122" data-mp="4z4pm08jkkmb">
        <div className="mx-auto max-w-5xl" data-id="element-123" data-mp="517s3jqvs67x">
          <SectionHeading light centered eyebrow="La cuenta real" title="¿Cuánto cuesta no tenerlo?" intro="Cada sesión no necesita ser perfecta. Pero no tenés por qué atravesar sola/o los momentos difíciles." data-id="element-124" data-mp="iz61sbu9beh3" />
          <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-125" data-mp="1pf89ypc1ap6z">
            {risks.map(risk => <p key={risk} className="flex items-center gap-3 border border-white/15 bg-white/5 p-4 text-sm text-[#E8E2F0]" data-id="element-126" data-mp="1xw8livsmycdv">
                <span className="h-2 w-2 shrink-0 bg-[#B895F2]" data-id="element-127" data-mp="21vhisamvwse6" />
                {risk}
              </p>)}
          </div>
          <p className="mt-8 text-center font-serif text-2xl text-[#DDCCF8]" data-id="element-128" data-mp="1swdxd008w0wi">
            El próximo lunes puede ser completamente diferente.
          </p>
        </div>
      </section>

      <section className="bg-[#F2EFF6] px-5 py-14 text-center sm:px-8 sm:py-20" data-id="element-129" data-mp="2llbev0wmtto7">
        <div className="mx-auto max-w-xl" data-id="element-130" data-mp="2lne0l8qr50n4">
          <HeartHandshakeIcon className="mx-auto h-9 w-9 text-[#6D28D9]" aria-hidden="true" data-id="element-131" data-mp="26xghh77oj14s" />
          <p className="mt-5 text-xs font-bold uppercase tracking-[.18em] text-[#6D28D9]" data-id="element-132" data-mp="2bu6wrsbntsp5">
            El Oráculo del Terapeuta™
          </p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-[#24212A]" data-id="element-133" data-mp="34bssrzct2kpi">
            Que la próxima sesión sea tuya, no del miedo.
          </h2>
          <p className="mt-4 text-sm leading-6 text-[#5E5968]" data-id="element-134" data-mp="37mokb0szk0lx">
            6 tomos · 6 bonos · acceso inmediato · garantía de 7 días
          </p>
          <div className="mt-7" data-id="element-135" data-mp="2yow3fs7m8kwf">
            <CTAButton data-id="element-136" data-mp="2ynkyvq6cj949">Quiero mi Oráculo ahora</CTAButton>
          </div>
          <p className="mt-5 text-xs text-[#5E5968]" data-id="element-137" data-mp="33mecdilmv04f">
            Pago 100% seguro · Tarjeta · PayPal · Transferencia
          </p>
        </div>
      </section>
    </>;
}
export function PurchaseNotification() {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), 8500);
    return () => window.clearTimeout(timer);
  }, []);
  if (!visible) return null;
  return <motion.aside initial={{
    opacity: 0,
    y: 18
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} className="fixed bottom-4 left-4 z-50 max-w-[280px] border border-[#DCD4E7] bg-[#FFFDFB] p-3 shadow-sm" aria-live="polite" data-id="element-138" data-mp="2jzgiisurn9on">
      <div className="flex gap-3" data-id="element-139" data-mp="1mdnbxnsj9ct8">
        <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#6D28D9]" aria-hidden="true" data-id="element-140" data-mp="1meoz2qmtpprb" />
        <p className="text-xs leading-5 text-[#514B5A]" data-id="element-141" data-mp="21ch9j4dijnyn">
          <strong className="block text-[#24212A]" data-id="element-142" data-mp="1wba43byuf0v5">Compra verificada</strong>
          Carolina M. de Guadalajara compró El Oráculo del Terapeuta · hace 8
          min
        </p>
      </div>
    </motion.aside>;
}